import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, AlertCircle, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SuchAuswahl from "@/components/ui/such-auswahl";
import {
    fehlendeMietverhaeltnisse, listUnits, listProperties, createTenant, generateCharges,
} from "@/lib/api";
import { germanDate, euro } from "@/lib/format";
import { Seitenkopf } from "@/hilfe/Seitenkopf";

// Schritt 4a des Fahrplans. Am 05.10.2026 stammten 204 von 292 unzugeordneten
// Mieteingaengen von Zahlern, die im System kein Mietverhaeltnis hatten -- es
// fehlte nicht die Zuordnung, es fehlte das Soll. Diese Seite zeigt die
// regelmaessigen Zahler ohne Mietverhaeltnis als Vorschlaege; angelegt wird
// erst, wenn ein Mensch bestaetigt und die Vertragsdaten ergaenzt hat.

const heuteMonat = () => new Date().toISOString().slice(0, 7);

// Monate vom Einzug bis heute, fruehestens ab 2026-01 (sauberer Start).
const monateAb = (einzug) => {
    const start = (einzug || "").slice(0, 7) < "2026-01" ? "2026-01" : (einzug || "").slice(0, 7);
    if (!start) return [];
    const out = [];
    let [j, m] = start.split("-").map(Number);
    const ende = heuteMonat();
    while (`${j}-${String(m).padStart(2, "0")}` <= ende && out.length < 24) {
        out.push(`${j}-${String(m).padStart(2, "0")}`);
        m += 1;
        if (m > 12) { j += 1; m = 1; }
    }
    return out;
};

const formAus = (v) => ({
    full_name: v.name_vorschlag || "",
    unit_id: v.unit_id || "",
    einzugsdatum: v.einzug_vorschlag || "",
    mietart: "kalt_nk",
    kaltmiete: v.betrag != null ? String(v.betrag) : "",
    nebenkosten: "",
    heizkosten: "",
    pauschalmiete: v.betrag != null ? String(v.betrag) : "",
    kaution: "",
    notes: `Aus Zahlungen vorgeschlagen (${v.konto?.bezeichnung || v.konto?.iban}, ${v.anzahl} Eingänge, `
        + `${v.betraege.map((b) => euro(b)).join(" / ")}; Zweck: ${v.zweck_beispiel || "—"})`,
});

const FehlendeMieter = () => {
    const [daten, setDaten] = useState(null);
    const [units, setUnits] = useState([]);
    const [props, setProps] = useState([]);
    const [laedt, setLaedt] = useState(true);
    const [offen, setOffen] = useState(null);
    const [forms, setForms] = useState({});
    const [busy, setBusy] = useState(null);

    const laden = async () => {
        setLaedt(true);
        try {
            const [d, u, p] = await Promise.all([fehlendeMietverhaeltnisse(), listUnits(), listProperties()]);
            setDaten(d); setUnits(u); setProps(p);
        } catch (e) {
            toast.error("Vorschläge nicht geladen", { description: e?.response?.data?.detail?.error });
        } finally { setLaedt(false); }
    };
    useEffect(() => { laden(); }, []);

    const propName = useMemo(() => Object.fromEntries(props.map((p) => [p.id, p.address])), [props]);
    const unitsSortiert = useMemo(() => [...units].sort((a, b) =>
        `${propName[a.property_id] || ""} ${a.unit_label || ""}`.localeCompare(
            `${propName[b.property_id] || ""} ${b.unit_label || ""}`, "de", { numeric: true })), [units, propName]);

    // Vorschlaege aus den Buchungen zuerst und mit Stern, danach alle uebrigen.
    // Ein Kandidat kann aus den Buchungen stammen und in der geladenen Liste
    // fehlen -- dann gilt er selbst.
    const einheitenOptionen = (v) => {
        const text = (u) => `${propName[u.property_id] || "?"} · ${u.unit_label || "?"}`;
        const vorschlaege = (v.unit_kandidaten || []).map((k) => units.find((u) => u.id === k.id) || k);
        const ids = new Set(vorschlaege.map((u) => u.id));
        return [
            ...vorschlaege.map((u) => ({ wert: u.id, text: text(u), hervorgehoben: true })),
            ...unitsSortiert.filter((u) => !ids.has(u.id)).map((u) => ({ wert: u.id, text: text(u) })),
        ];
    };

    const form = (v) => forms[v.key] || formAus(v);
    const setFeld = (v, feld, wert) => setForms((f) => ({ ...f, [v.key]: { ...form(v), [feld]: wert } }));

    const anlegen = async (v) => {
        const f = form(v);
        if (!f.full_name.trim()) { toast.error("Name fehlt"); return; }
        if (!f.unit_id) { toast.error("Einheit wählen — ohne Einheit kein Soll"); return; }
        if (!f.einzugsdatum) { toast.error("Einzugsdatum fehlt"); return; }
        setBusy(v.key);
        try {
            const zahl = (x) => (x === "" || x == null ? null : parseFloat(x));
            const t = await createTenant({
                full_name: f.full_name.trim(), unit_id: f.unit_id, status: "aktiv",
                mietart: f.mietart,
                kaltmiete: f.mietart === "kalt_nk" ? zahl(f.kaltmiete) : null,
                nebenkosten: f.mietart === "kalt_nk" ? zahl(f.nebenkosten) : null,
                heizkosten: f.mietart === "kalt_nk" ? zahl(f.heizkosten) : null,
                pauschalmiete: f.mietart === "pauschal" ? zahl(f.pauschalmiete) : null,
                kaution: zahl(f.kaution), einzugsdatum: f.einzugsdatum, notes: f.notes,
            });
            // Soll nur fuer dieses Mietverhaeltnis, Monat fuer Monat -- der Lauf
            // legt sonst fuer alle anderen fehlende Monate nach.
            let positionen = 0, summe = 0;
            for (const period of monateAb(f.einzugsdatum)) {
                const r = await generateCharges(period, true, t.id);
                positionen += r.neu || 0; summe += r.soll_summe_neu || 0;
            }
            toast.success(`${f.full_name} angelegt — ${positionen} Soll-Positionen, ${euro(summe)}`, {
                description: "Der Zuordnungsvorschlag im Mietkonto findet die Zahlungen jetzt.",
            });
            setOffen(null);
            await laden();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.error || "Anlegen fehlgeschlagen",
                        { description: typeof d === "object" ? d?.detail : null });
        } finally { setBusy(null); }
    };

    const vs = daten?.vorschlaege || [];
    const ohneMieter = daten?.einheiten_vermietet_ohne_mieter || [];

    return (
        <div className="px-6 md:px-10 py-8 space-y-6" data-testid="fehlende-mieter">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <Link to="/mieter" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-slate-900 mb-2">
                        <ArrowLeft size={12} /> Mieter
                    </Link>
                    <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="fehlende-titel">
                        Fehlende Mietverhältnisse
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Regelmäßige Zahler auf Mietkonten, deren Name auf keinen Mieter im System passt.
                        Jeder Vorschlag nennt Einheit, Betrag und frühesten Monat aus den Buchungen —
                        du bestätigst, ergänzt die Vertragsdaten, und das Soll entsteht ab dem Einzug.
                        Angelegt wird nichts von selbst.
                    </p>
                    <Seitenkopf id="seite.fehlende_mieter" />
                </div>
                <Button variant="outline" size="sm" onClick={laden} disabled={laedt} grund="Wird geladen" data-testid="fehlende-neu-laden">
                    <RefreshCw size={13} className={`mr-1.5 ${laedt ? "animate-spin" : ""}`} /> Neu laden
                </Button>
            </div>

            {daten && (
                <div className="flex items-center gap-3 text-xs text-gray-500" data-testid="fehlende-zaehler">
                    <span><b className="text-slate-900">{daten.anzahl_vorschlaege}</b> Vorschläge</span>
                    <span>·</span>
                    <span>{daten.eingaenge_in_vorschlaegen} Eingänge, {euro(daten.summe_in_vorschlaegen)}</span>
                    <span>·</span>
                    <span>seit {germanDate(daten.von)} · mindestens {daten.min_eingaenge} Eingänge</span>
                    {ohneMieter.length > 0 && (<><span>·</span>
                        <span className="text-orange-700">{ohneMieter.length} Einheiten „vermietet" ohne Mieter</span></>)}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-[0.14em] text-gray-500">
                        <tr>
                            <th className="px-3 py-2 text-left w-6"></th>
                            <th className="px-3 py-2 text-left">Zahler / Name</th>
                            <th className="px-3 py-2 text-left">Konto</th>
                            <th className="px-3 py-2 text-left">Objekt / Einheit</th>
                            <th className="px-3 py-2 text-right">Betrag</th>
                            <th className="px-3 py-2 text-right">Eingänge</th>
                            <th className="px-3 py-2 text-left">ab</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {vs.map((v) => {
                            const auf = offen === v.key;
                            const f = form(v);
                            return (
                                <React.Fragment key={v.key}>
                                    <tr className="hover:bg-gray-50/60 cursor-pointer" onClick={() => setOffen(auf ? null : v.key)}
                                        data-testid={`fehlend-${v.key}`}>
                                        <td className="px-3 py-2 text-gray-400">{auf ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                                        <td className="px-3 py-2">
                                            <div className="text-slate-900 font-medium">{v.name_vorschlag}</div>
                                            {v.quelle_name === "verwendungszweck" && (
                                                <div className="text-[11px] text-gray-500">zahlt über {v.zahler}</div>
                                            )}
                                            {v.hinweise.filter((h) => !h.startsWith("zahlt über")).map((h) => (
                                                <div key={h} className="text-[11px] text-amber-700 mt-0.5 inline-flex items-start gap-1">
                                                    <AlertCircle size={11} className="mt-0.5 shrink-0" />{h}
                                                </div>
                                            ))}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-600">
                                            <div>{v.konto.bezeichnung || "—"}</div>
                                            <div className="text-[10px] text-gray-400">{v.konto.ebene || "ohne Zuordnung"}</div>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-700">
                                            <div>{v.property_address || <span className="text-gray-400">unbestimmt</span>}</div>
                                            <div className="text-gray-500">{v.unit_label || (v.unit_kandidaten.length > 1 ? `${v.unit_kandidaten.length} Kandidaten` : "—")}</div>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                                            {euro(v.betrag)}
                                            {v.betraege.length > 1 && <div className="text-[10px] text-gray-400">{v.betraege.length} verschiedene</div>}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums text-gray-600">{v.anzahl} <span className="text-gray-400">/ {v.monate} Mon.</span></td>
                                        <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{germanDate(v.erster)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <Button size="sm" variant={auf ? "default" : "outline"} className="h-7 text-xs"
                                                    onClick={(e) => { e.stopPropagation(); setOffen(auf ? null : v.key); }}>
                                                <UserPlus size={12} className="mr-1" /> {auf ? "Schließen" : "Anlegen"}
                                            </Button>
                                        </td>
                                    </tr>
                                    {auf && (
                                        <tr className="bg-gray-50/70">
                                            <td></td>
                                            <td colSpan={7} className="px-3 py-3">
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`fehlend-form-${v.key}`}>
                                                    <div className="col-span-2">
                                                        <Label className="text-xs">Name</Label>
                                                        <Input value={f.full_name} onChange={(e) => setFeld(v, "full_name", e.target.value)} />
                                                    </div>
                                                    <div className="col-span-2">
                                                        <Label className="text-xs">Einheit</Label>
                                                        <SuchAuswahl
                                                            wert={f.unit_id}
                                                            onWaehlen={(x) => setFeld(v, "unit_id", x)}
                                                            optionen={einheitenOptionen(v)}
                                                            leerText="Auswahl aufheben"
                                                            testId={`einheit-waehler-${v.key}`}
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs">Einzug (ab spätestens)</Label>
                                                        <Input type="date" value={f.einzugsdatum} onChange={(e) => setFeld(v, "einzugsdatum", e.target.value)} />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs">Mietart</Label>
                                                        <SuchAuswahl wert={f.mietart} onWaehlen={(x) => setFeld(v, "mietart", x)}
                                                            optionen={[
                                                                { wert: "kalt_nk", text: "Kalt + Nebenkosten" },
                                                                { wert: "pauschal", text: "Pauschal" },
                                                            ]}
                                                        />
                                                    </div>
                                                    {f.mietart === "kalt_nk" ? (
                                                        <>
                                                            <div>
                                                                <Label className="text-xs">Kaltmiete</Label>
                                                                <Input type="number" step="0.01" value={f.kaltmiete} onChange={(e) => setFeld(v, "kaltmiete", e.target.value)} />
                                                            </div>
                                                            <div>
                                                                <Label className="text-xs">Nebenkosten-VZ</Label>
                                                                <Input type="number" step="0.01" value={f.nebenkosten} onChange={(e) => setFeld(v, "nebenkosten", e.target.value)} />
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div>
                                                            <Label className="text-xs">Pauschalmiete</Label>
                                                            <Input type="number" step="0.01" value={f.pauschalmiete} onChange={(e) => setFeld(v, "pauschalmiete", e.target.value)} />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <Label className="text-xs">Kaution</Label>
                                                        <Input type="number" step="0.01" value={f.kaution} onChange={(e) => setFeld(v, "kaution", e.target.value)} />
                                                    </div>
                                                    <div className="col-span-2 md:col-span-4">
                                                        <Label className="text-xs">Notiz</Label>
                                                        <Input value={f.notes} onChange={(e) => setFeld(v, "notes", e.target.value)} />
                                                    </div>
                                                    <div className="col-span-2 md:col-span-4 flex items-center justify-between gap-3">
                                                        <div className="text-[11px] text-gray-500">
                                                            Legt das Mietverhältnis an und erzeugt das Soll von {germanDate(f.einzugsdatum)} bis heute
                                                            ({monateAb(f.einzugsdatum).length} Monate) — nur für dieses Mietverhältnis.
                                                            Die Gesamtmiete laut Buchungen: {euro(v.betrag)}; verteile sie auf Kalt und Nebenkosten.
                                                        </div>
                                                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-8"
                                                                onClick={() => anlegen(v)} disabled={busy === v.key} grund="Wird angelegt"
                                                                data-testid={`fehlend-anlegen-${v.key}`}>
                                                            <UserPlus size={12} className="mr-1.5" /> Anlegen und Soll erzeugen
                                                        </Button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {!vs.length && (
                            <tr><td colSpan={8} className="px-3 py-12 text-center text-sm text-gray-400">
                                {laedt ? "Lädt …" : "Kein regelmäßiger Zahler ohne Mietverhältnis — der Nenner ist vollständig."}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {ohneMieter.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-md p-5" data-testid="fehlende-ohne-mieter">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-3">
                        Einheiten „vermietet" ohne Mieter und ohne Soll 2026
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
                        {ohneMieter.map((u) => (
                            <Link key={u.unit_id} to={`/einheiten/${u.unit_id}`}
                                  className="flex items-baseline justify-between gap-2 border border-gray-100 rounded px-3 py-2 hover:border-slate-400">
                                <span className="text-slate-900">{u.property_address}</span>
                                <span className="text-gray-500 text-xs">{u.unit_label}</span>
                            </Link>
                        ))}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-3">
                        Entweder gehört ein Vorschlag von oben hierher — dann die Einheit dort wählen —
                        oder der Status „vermietet" stimmt nicht.
                    </div>
                </div>
            )}
        </div>
    );
};

export default FehlendeMieter;
